// @ts-ignore
import {Course} from "@/types/Course"
import type {Ref, UnwrapRef} from "vue"
import moment from "moment/moment"
import type {CourseShared} from "@/types/CourseShared";

export type TypeNameMap = {
    [key: string]: string
}

export const COURSE_TYPE_NAME_MAP: TypeNameMap = {
    A: "Advanced Training",
    G: "Basic Training/Orientation Course",
    M: "In-mission/In-service Training",
    P: "Pre-deployment Training",
    R: "Pre-posting Training",
    S: "Specialised Training",
    X: "Safety and Security Training"
}

export const AUDIENCE_LEVELS: TypeNameMap = {
    O: 'Operational',
    P: 'Political-strategic',
    R: 'Other',
    S: 'Strategic',
    T: 'Tactical'
}

/**
 * ------------------------------------------------
 * General Course Page Functions
 * ------------------------------------------------
 */
export function getCourseTypeName(courseType: string): string {
    return COURSE_TYPE_NAME_MAP[courseType] || "Unknown"
}


export function formatLocation(city: string | undefined, country: string | undefined) {
    return [city, country].filter(Boolean).join(', ')
}


export function formatContactType(contact: any) {
    const types = []
    if (contact.isForRegistration) {
        types.push('Registration')
    }
    if (contact.isForAdministrativeMatters) {
        types.push('Administrative Matters')
    }
    if (contact.isForTraining) {
        types.push('Training')
    }
    return types.join(', ')
}

export function concatenateStringArray(array: string[]): string {
    return array.join(", ")
}

export function formatAudienceLevels(array: string[]): string {
    let result: string
    if(array.includes("All")) {
        // return the entire list of Audience Levels
        result = Object.values(AUDIENCE_LEVELS).join(', ')
    } else {
        result = concatenateStringArray(array)
    }
    return result
}


/**
 * Item to be printed in the front-end must contain a property name or description
 * @param list[] with structure {name: string, description: string} []
 * @param property to be concatenated
 */
export function concatenateObjectPropertyArray(list: { name: string, description: string }[], property: string): string {
    if (!list) {
        return ''
    }
    let result = ""
    if (property === "name") {
        result = list.map((item: { name: string, description: string }) => item.name).join(', ')
    } else if (property === "description") {
        result = list.map((item: { name: string, description: string }) => item.description).join(', ')
    }
    return result
}

export function formatProviders(list: { name: string, compartment: { name: string }}[]): string {
    if (!list) {
        return ''
    }
    const formattedItems: string[] = list.map((item) => `${item.name} (${item.compartment.name})`)
    return formattedItems.join(', ')
}

export function formatDate(date: Date): string {
    return moment(date).format('D MMM YYYY')
}


export function isCourseLastCall(theDeadline: UnwrapRef<Course["deadline"]> | undefined): boolean {

    let result: boolean = false;
    if (theDeadline != null || theDeadline != undefined) {
        const today = new Date( new Date().getTime());
        today.setTime(today.setHours(0,0,0, 0));
        const deadline = new Date( new Date(theDeadline).getTime() );
        deadline.setTime(deadline.setHours(0,0,0, 0));
        result = today.getTime() >=  new Date (deadline.getTime() - (14*24*60*60*1000) ).getTime();
    }
    return result;

}


/**
 * ------------------------------------------------
 * Bookmarks Functions
 * ------------------------------------------------
 */

export function getBookmarks(): number[] {
    return JSON.parse(localStorage.getItem('bookmarks') || '[]') as number[];
}


export function isCourseBookmarked(id: number | undefined | null): boolean {

    let result: boolean = false;
    if (id != null) {
        result = getBookmarks().includes(id);
    }
    return result;

}


export function toggleBookmark(id: number): boolean {

    let bookmarked: boolean;
    let bookmarks: number[] = getBookmarks();

    if (bookmarks.includes(id)) {
        bookmarks = bookmarks.filter((item) => item !== id);
        bookmarked = false;
    } else {
        bookmarks.push(id);
        bookmarked = true;
    }
    localStorage.setItem("bookmarks", JSON.stringify(bookmarks));

    return bookmarked;

}

export function updateBookmarks(ids: number[]) {

    localStorage.setItem("bookmarks", JSON.stringify(ids));

}


export function getBookmarksCount(): number {
    return getBookmarks().length;
}


/**
 * ------------------------------------------------
 * Calendar Functions
 * ------------------------------------------------
 */
export function iCalendarDesc(course: Ref<UnwrapRef<Course | null>>): string {

    let desc: string = ''
    if (course.value?.network && course.value?.network?.name) {
        desc += `[strong]Network:[/strong] ${course.value?.network?.name}`
    }
    desc += `[br][strong]Provider:[/strong] ${course.value?.provider?.name} (${course.value?.provider?.compartment?.name})`
    desc += `[br][strong]Link:[/strong] ${course.value?.applicationUrl}/course/${course.value?.id}\n`

    return desc
}


/**
 * ------------------------------------------------
 * Share by Email Functions
 * ------------------------------------------------
 */



export function shareCourse(course: Course){
    const encodedSubject = encodeURIComponent("CSDP Training Programme: " + (course.title ?? ''))
    const encodedBody = getEncodedBody(course);
    window.location.replace(`mailto:?subject=${encodedSubject}&body=${encodedBody}`)
}

/**
 * Format the body of the email according to the limitation in length of the mailTo HTML directive
 * @param course
 */
export function getEncodedBody(course: Course|CourseShared): string {

    const courseUrl = `course/${course.id}`
    const maxBodyLength = 2000 - encodeURIComponent('mailto:?subject=' + (course.title ?? '')).length
    const compartmentName: string = course.provider.compartment != undefined ? course.provider.compartment.name : course.provider.compartmentName

    let preAim = `A course has been shared from Schoolmaster.\n\nCourse title: `
        + `${course.title}\nProvider: ${course.provider?.name} (${compartmentName})`
    if (course.network?.name) {
        preAim += `\nNetwork: ${course.network?.name}`
    }

    const venueCountryName: string = course.venueCountry.name != undefined ? course.venueCountry.name : course.venueCountry
    const location = formatLocation(course?.venueCity, venueCountryName)
    const from = course.dateFrom ? formatDate(course.dateFrom) : null
    const to = course.dateTo ? formatDate(course.dateTo) : null
    const deadline = course.deadline ? formatDate(course.deadline) : null

    preAim += `\nLocation: ${location}\nStart - End date: ${from} to ${to}\n` +
        `Application deadline: ${deadline}\n\n` +
        `Click on the following link to view the course: ${course.applicationUrl}/${courseUrl}\n`;

    let availableLength = maxBodyLength - encodeURIComponent(preAim).length

    let overallAim = `Overall aim: \n${course.overallAim ?? ''}`
    let preRequisites = `\n\nPre-requisites: \n${course.preRequisites ?? ''}\n`
    let totalLength = encodeURIComponent(overallAim + preRequisites).length

    if (totalLength > availableLength) {
        let ratio = availableLength / totalLength
        overallAim = overallAim.slice(0, Math.floor(ratio * overallAim.length))
        preRequisites = preRequisites.slice(0, Math.floor(ratio * preRequisites.length))
        totalLength = encodeURIComponent(overallAim + preRequisites).length

        // if still over length, trim both proportionally
        while (totalLength > availableLength) {
            overallAim = overallAim.slice(0, overallAim.length - 1)
            preRequisites = preRequisites.slice(0, preRequisites.length - 1)
            totalLength = encodeURIComponent(overallAim + preRequisites).length
        }
    }

    let body = preAim + overallAim.slice(0, overallAim.length - 3) + "..." + preRequisites.slice(0, preRequisites.length - 3) + "..."
    return encodeURIComponent(body)
}



